import React from 'react';
import Page from 'Core/components/Page';
import { useTranslation } from 'react-i18next';
import { useScrollTop } from 'Core/hooks/useScrollTop';
import Card from 'Core/components/FormElements/Card';
import useErrorStore, { errorSelector } from 'Core/stores/error';
import { Alert } from 'Core/components/Alert';
import paperworkIllustration from 'Images/illustrations/paperwork.svg';

export function Error() {
  document.title = 'Payment Portal - Application Not Found';
  useScrollTop();
  const { t } = useTranslation();
  const error = useErrorStore(errorSelector);

  return (
    <Page title={t('easyCredit.page.financing.header')} withStepProgress={false} stickyFooter hideAlert>
      <div className="mt-12 max-w-4xl mx-auto">
        <Card className="bg-gradient-to-br from-white to-gray-50 shadow-xl border-0 overflow-hidden">
          {/* Top accent bar */}
          <div className="h-2 bg-gradient-to-r from-red-400 via-rose-400 to-red-500"></div>
          
          <div className="p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Illustration */}
              <div className="flex-shrink-0 w-40 md:w-56">
                <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-2xl p-6 shadow-inner">
                  <img 
                    src={paperworkIllustration} 
                    alt="Paperwork illustration" 
                    className="w-full h-auto"
                  />
                </div>
              </div>
              
              {/* Content */}
              <div className="flex-1 text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 text-red-800 rounded-full text-sm font-semibold mb-4">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  Error
                </div>
                
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                  {t('easyCredit.applicationNotFound.title')}
                </h2>
                
                <p className="text-lg text-gray-600 leading-relaxed">
                  {t('easyCredit.applicationNotFound.content')}
                </p>
              </div>
            </div>

            {/* Error Details Section */}
            {error && error.text && (
              <div className="mt-10 pt-8 border-t border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-gray-700 font-semibold text-lg">Error Details</p>
                </div>

                <Alert data-testid={`alert-${error.type}`} type={error.type} showIcon>
                  {error.text}
                </Alert>
              </div>
            )}
          </div>
        </Card>
      </div>
    </Page>
  );
}
